<template>
    <base-modal :is-form-visible="isVisible" @close="onCancel">
        <template #header>
            <h2 class="text-lg">
                {{ mode === 'pause' ? 'Pause Engagement' : 'Resume Engagement' }}
            </h2>
        </template>
        <template #body>
            <div class="p-2 min-w-[28rem]">
                <p class="text-sm text-gray-700 mb-3">
                    <template v-if="mode === 'pause'">
                        Pausing will stop next-invoice calculations until resumed.
                        Please record why this engagement is being paused.
                    </template>
                    <template v-else>
                        Please record why this engagement is being resumed.
                    </template>
                </p>
                <label for="pauseReason" class="block text-sm font-medium mb-1">
                    Reason <span class="text-red-600">*</span>
                </label>
                <textarea
                    id="pauseReason"
                    v-model="reason"
                    rows="4"
                    class="w-full p-2 border border-gray-400 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    :placeholder="mode === 'pause' ? 'e.g. Client has a dispute about scope; pausing until resolved.' : 'e.g. Dispute resolved; resuming monthly retainer.'"
                ></textarea>
                <div v-if="errorMessage" class="mt-2 text-sm text-red-600">
                    {{ errorMessage }}
                </div>
                <div class="flex justify-end space-x-2 mt-4">
                    <base-button-negative @click.prevent="onCancel" :disabled="isSubmitting">Cancel</base-button-negative>
                    <base-button-affirmative @click.prevent="onConfirm" :disabled="isSubmitting || !reason.trim()">
                        {{ confirmLabel }}
                    </base-button-affirmative>
                </div>
            </div>
        </template>
    </base-modal>
</template>

<script>
export default {
    name: 'engagement-pause-modal'
}
</script>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import { pauseEngagement, unpauseEngagement } from '@/services/Engagement.js';

const props = defineProps({
    isVisible: { type: Boolean, default: false },
    engagementId: { type: Number, required: true },
    mode: { type: String, required: true, validator: (v) => ['pause', 'unpause'].includes(v) },
})

const emit = defineEmits(['close', 'success'])

const store = useStore()
const $toast = useToast()

const reason = ref('')
const isSubmitting = ref(false)
const errorMessage = ref('')

const confirmLabel = computed(() => {
    if (isSubmitting.value) return props.mode === 'pause' ? 'Pausing…' : 'Resuming…'
    return props.mode === 'pause' ? 'Pause' : 'Resume'
})

watch(() => props.isVisible, (visible) => {
    if (visible) {
        reason.value = ''
        errorMessage.value = ''
        isSubmitting.value = false
    }
})

function onCancel() {
    if (isSubmitting.value) return
    emit('close')
}

async function onConfirm() {
    const trimmed = reason.value.trim()
    if (!trimmed) {
        errorMessage.value = 'A reason is required.'
        return
    }
    isSubmitting.value = true
    errorMessage.value = ''
    const token = store.state.auth.token
    try {
        const fn = props.mode === 'pause' ? pauseEngagement : unpauseEngagement
        const response = await fn(props.engagementId, trimmed, token)
        if (response && response.status >= 200 && response.status < 300) {
            $toast.open({
                message: props.mode === 'pause'
                    ? 'Engagement paused.'
                    : 'Engagement resumed.',
                type: 'success',
                duration: 3000,
            })
            emit('success', response.data)
            emit('close')
        } else {
            const detail = (response && response.data && response.data.detail) || 'Request failed.'
            errorMessage.value = detail
        }
    } catch (err) {
        const detail = err && err.response && err.response.data && err.response.data.detail
        errorMessage.value = detail || 'Network error — please try again.'
    } finally {
        isSubmitting.value = false
    }
}
</script>
